import React, { useCallback, useEffect, useState } from "react";
import { Form, Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import bmsService, { BmsServiceItem } from "../../features/bms/bmsService";
import { TbBriefcase, TbEdit, TbPlus, TbTrash, TbX, TbCheck } from "react-icons/tb";
import BmsChatbotSelector from "../../components/bms/BmsChatbotSelector";
import BmsPagination from "../../components/bms/BmsPagination";
import { showSuccess, showError, showConfirm } from "../../utils/bmsToast";

const PAGE_SIZE = 25;

interface ServiceForm {
  name: string;
  description: string;
  duration_minutes: number;
  is_active: boolean;
}

const emptyForm: ServiceForm = { name: "", description: "", duration_minutes: 30, is_active: true };

const BmsServicesPage: React.FC = () => {
  const { t } = useTranslation();
  const [chatbotId, setChatbotId] = useState<number | null>(null);
  const [data, setData] = useState<BmsServiceItem[]>([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [isLoading, setIsLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState<BmsServiceItem | null>(null);
  const [form, setForm] = useState<ServiceForm>(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!chatbotId) { setData([]); setTotal(0); return; }
    setIsLoading(true);
    try {
      const res = await bmsService.listServices(chatbotId, { limit, skip });
      setData(res.data || []);
      setTotal(res.total ?? 0);
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || "Failed to load services");
    } finally { setIsLoading(false); }
  }, [chatbotId, limit, skip]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSkip(0); }, [chatbotId]);

  const openCreate = () => { setEditingService(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (s: BmsServiceItem) => {
    setEditingService(s);
    setForm({ name: s.name, description: s.description || "", duration_minutes: s.duration_minutes, is_active: !!s.is_active });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!chatbotId || !form.name.trim()) return;
    setSaving(true);
    try {
      if (editingService) {
        await bmsService.updateService(chatbotId, editingService.id, {
          name: form.name, description: form.description || undefined,
          duration_minutes: form.duration_minutes, is_active: form.is_active,
        });
        showSuccess(t("bms_service_updated"));
      } else {
        await bmsService.createService(chatbotId, {
          name: form.name, description: form.description || undefined,
          duration_minutes: form.duration_minutes, is_active: form.is_active,
        });
        showSuccess(t("bms_service_created"));
      }
      setShowModal(false);
      await load();
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || "Error");
    } finally { setSaving(false); }
  };

  const handleDeactivate = async (service: BmsServiceItem) => {
    if (!chatbotId) return;
    const confirmed = await showConfirm({
      title: t("bms_confirm_title"), text: t("bms_confirm_deactivate_service"),
      confirmText: t("bms_confirm_yes"), cancelText: t("bms_confirm_no"),
    });
    if (!confirmed) return;
    try {
      await bmsService.deleteService(chatbotId, service.id);
      showSuccess(t("bms_service_deactivated"));
      await load();
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || "Error");
    }
  };

  return (
    <div className="chatbots-page bms-page">
      <div className="page-header-card mb-3">
        <div className="page-header-text">
          <h1 className="d-flex align-items-center gap-2"><TbBriefcase /> {t("bms_services")}</h1>
          <p>{t("bms_services_desc")}</p>
        </div>
      </div>

      <div className="p-3 pt-0">
        <BmsChatbotSelector chatbotId={chatbotId} onChatbotChange={setChatbotId} />

        <div className="bms-section-card mt-3">
          <div className="bms-section-header">
            <div className="bms-section-title">
              <span className="bms-section-icon"><TbBriefcase /></span>
              {t("bms_services")}
            </div>
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>{total} {t("records")}</span>
              <button className="bms-btn-primary" onClick={openCreate} disabled={!chatbotId}>
                <TbPlus /> {t("bms_add_service")}
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="d-flex justify-content-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : data.length === 0 ? (
            <div className="bms-empty">
              <div className="bms-empty-icon"><TbBriefcase /></div>
              <p>{t("bms_no_data")}</p>
            </div>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table className="bms-table table mb-0">
                  <thead>
                    <tr>
                      <th>{t("bms_service_name")}</th>
                      <th>{t("bms_service_description")}</th>
                      <th>{t("bms_service_duration")}</th>
                      <th>{t("bms_status")}</th>
                      <th style={{ width: 90 }}>{t("bms_actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((s) => (
                      <tr key={s.id}>
                        <td className="fw-semibold">{s.name}</td>
                        <td><span className="text-muted">{s.description || "—"}</span></td>
                        <td>{s.duration_minutes} {t("bms_minutes_short")}</td>
                        <td><span className={`bms-status ${s.is_active ? "active" : "inactive"}`}>{s.is_active ? t("active") : t("inactive")}</span></td>
                        <td>
                          <div className="d-flex gap-1">
                            <button className="bms-action-btn edit" onClick={() => openEdit(s)} title={t("edit")}><TbEdit /></button>
                            {!!s.is_active && <button className="bms-action-btn danger" onClick={() => handleDeactivate(s)} title={t("delete")}><TbTrash /></button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <BmsPagination total={total} skip={skip} limit={limit} onPageChange={setSkip} onLimitChange={(v) => { setLimit(v); setSkip(0); }} />
            </>
          )}
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} className="bms-modal" centered>
        <Modal.Header closeButton className="bms-modal-header">
          <Modal.Title className="d-flex align-items-center gap-2">
            {editingService ? <TbEdit /> : <TbPlus />} {editingService ? t("bms_edit_service") : t("bms_add_service")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3"><Form.Label className="bms-form-label">{t("bms_service_name")} *</Form.Label><Form.Control value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} maxLength={255} /></Form.Group>
          <Form.Group className="mb-3"><Form.Label className="bms-form-label">{t("bms_service_description")}</Form.Label><Form.Control as="textarea" rows={2} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} maxLength={1000} /></Form.Group>
          <Form.Group className="mb-3"><Form.Label className="bms-form-label">{t("bms_service_duration")} *</Form.Label><Form.Control type="number" min={5} max={480} value={form.duration_minutes} onChange={(e) => setForm((p) => ({ ...p, duration_minutes: Number(e.target.value) }))} /></Form.Group>
          <Form.Check type="switch" label={t("bms_service_is_active")} checked={form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} className="mt-2" />
        </Modal.Body>
        <Modal.Footer className="bms-modal-footer">
          <button className="bms-btn-secondary" onClick={() => setShowModal(false)}><TbX /> {t("cancel")}</button>
          <button className="bms-btn-primary" onClick={handleSave} disabled={saving || !form.name.trim()}>{saving ? <Spinner size="sm" animation="border" /> : <TbCheck />} {t("save")}</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BmsServicesPage;
